import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BankAccount, UpdateBankAccountRequest, Currency } from '../types/company';
import { companyService } from '../services/companyService';
import { wfirmaAccountService } from '../services/wfirmaAccountService';
import { integrationService } from '../services/integrationService';
import { useTranslation } from '../hooks/useTranslation';
import { WFirmaCompanyAccount } from '../types/wfirmaCompanyAccount';

interface EditBankAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  bankAccount: BankAccount | null;
  companyId: string;
  onSuccess: () => void;
}

const EditBankAccountModal: React.FC<EditBankAccountModalProps> = ({
  isOpen,
  onClose,
  bankAccount,
  companyId,
  onSuccess
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    bankName: '',
    accountNumber: '',
    swift: '',
    iban: '',
    correspondentAccount: '',
    correspondentBank: '',
    bankAddress: '',
    currency: Currency.PLN,
    balance: 0
  });
  const [selectedWFirmaAccountId, setSelectedWFirmaAccountId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Проверяем наличие конфигурации wFirma
  const { data: hasWFirmaConfig = false } = useQuery({
    queryKey: ['wfirma-config', companyId],
    queryFn: () => integrationService.hasWFirmaConfig(companyId),
    enabled: isOpen && !!companyId,
    retry: false
  });

  // Загружаем счета wFirma
  const { data: wfirmaAccounts = [] } = useQuery({
    queryKey: ['wfirma-accounts', companyId],
    queryFn: () => wfirmaAccountService.getCompanyAccounts(companyId),
    enabled: isOpen && hasWFirmaConfig && !!companyId
  });

  // Загружаем текущую связь
  const { data: mappingsData } = useQuery({
    queryKey: ['wfirma-account-mappings', companyId],
    queryFn: () => wfirmaAccountService.getMappings(companyId),
    enabled: isOpen && hasWFirmaConfig && !!companyId && !!bankAccount
  });

  // Мутация для связи счета
  const mapAccountMutation = useMutation({
    mutationFn: ({ bankAccountId, wfirmaAccountId }: { bankAccountId: string; wfirmaAccountId: string }) =>
      wfirmaAccountService.mapAccount(companyId, bankAccountId, wfirmaAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wfirma-account-mappings', companyId] });
    }
  });

  // Мутация для удаления связи
  const unmapAccountMutation = useMutation({
    mutationFn: (bankAccountId: string) => wfirmaAccountService.unmapAccount(companyId, bankAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wfirma-account-mappings', companyId] });
      setSelectedWFirmaAccountId('');
    }
  });

  useEffect(() => {
    if (bankAccount) {
      setFormData({
        name: bankAccount.name || '',
        bankName: bankAccount.bankName || '',
        accountNumber: bankAccount.accountNumber || '',
        swift: bankAccount.swift || '',
        iban: bankAccount.iban || '',
        correspondentAccount: bankAccount.correspondentAccount || '',
        correspondentBank: bankAccount.correspondentBank || '',
        bankAddress: bankAccount.bankAddress || '',
        currency: bankAccount.currency || Currency.PLN,
        balance: bankAccount.balance || 0
      });
    }
  }, [bankAccount]);

  // Устанавливаем текущую связь при загрузке
  useEffect(() => {
    if (mappingsData && bankAccount) {
      const wfirmaAccountId = mappingsData[bankAccount.id];
      if (wfirmaAccountId) {
        setSelectedWFirmaAccountId(wfirmaAccountId);
      } else {
        setSelectedWFirmaAccountId('');
      }
    }
  }, [mappingsData, bankAccount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bankAccount) return;

    setIsLoading(true);
    setError(null);

    try {
      const updateRequest: UpdateBankAccountRequest = {
        name: formData.name,
        bankName: formData.bankName,
        accountNumber: formData.accountNumber,
        swift: formData.swift,
        iban: formData.iban,
        correspondentAccount: formData.correspondentAccount,
        correspondentBank: formData.correspondentBank,
        bankAddress: formData.bankAddress,
        currency: formData.currency,
        balance: formData.balance
      };

      const updatedAccount = await companyService.updateBankAccount(companyId, bankAccount.id, updateRequest);
      
      // Обновляем связь с wFirma, если выбрана
      if (hasWFirmaConfig && selectedWFirmaAccountId) {
        try {
          await mapAccountMutation.mutateAsync({
            bankAccountId: bankAccount.id,
            wfirmaAccountId: selectedWFirmaAccountId
          });
        } catch (err) {
          // Не блокируем сохранение счета, если не удалось связать с wFirma
          console.error('Failed to link account with wFirma:', err);
        }
      } else if (hasWFirmaConfig && mappingsData && mappingsData[bankAccount.id] && !selectedWFirmaAccountId) {
        // Удаляем связь, если она была, но теперь не выбрана
        try {
          await unmapAccountMutation.mutateAsync(bankAccount.id);
        } catch (err) {
          console.error('Failed to unlink account from wFirma:', err);
        }
      }
      
      // Инвалидируем кэш компании, чтобы обновить данные
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || t('companies.accounts.updateError') || 'Ошибка при обновлении счета');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!bankAccount) return;
    
    if (!window.confirm(t('common.deleteAccountConfirm') || 'Вы уверены, что хотите удалить этот банковский счет?')) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await companyService.deleteBankAccount(companyId, bankAccount.id);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || t('companies.accounts.deleteError') || 'Ошибка при удалении счета');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !bankAccount) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 my-auto max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('common.editAccount') || 'Редактировать банковский счет'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('companies.bankAccountName') || 'Название счета'} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                placeholder={t('companies.bankAccountNamePlaceholder') || 'Например: Основной счет'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.bankName') || 'Название банка'}
              </label>
              <input
                type="text"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.accountNumber') || 'Номер счета'}
              </label>
              <input
                type="text"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.bankAccounts.swift') || 'SWIFT'}
              </label>
              <input
                type="text"
                value={formData.swift}
                onChange={(e) => setFormData({ ...formData, swift: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.bankAccounts.iban') || 'IBAN'}
              </label>
              <input
                type="text"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.correspondentAccount')}
                </label>
                <input
                  type="text"
                  value={formData.correspondentAccount}
                  onChange={(e) => setFormData({ ...formData, correspondentAccount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="10901014"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companies.correspondentBank')}
                </label>
                <input
                  type="text"
                  value={formData.correspondentBank}
                  onChange={(e) => setFormData({ ...formData, correspondentBank: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="National Bank of Poland"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('companies.bankAddress')}
              </label>
              <textarea
                value={formData.bankAddress}
                onChange={(e) => setFormData({ ...formData, bankAddress: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ul. Puławska 15, Warsaw, Poland 02-515"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.currency') || 'Валюта'}
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value as Currency })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={Currency.PLN}>{t('invoices.currencies.PLN') || 'PLN - Польский злотый'}</option>
                <option value={Currency.USD}>{t('invoices.currencies.USD') || 'USD - Доллар США'}</option>
                <option value={Currency.EUR}>{t('invoices.currencies.EUR') || 'EUR - Евро'}</option>
                <option value={Currency.GBP}>{t('invoices.currencies.GBP') || 'GBP - Фунт стерлингов'}</option>
                <option value={Currency.CAD}>{t('invoices.currencies.CAD') || 'CAD - Канадский доллар'}</option>
                <option value={Currency.CNY}>{t('invoices.currencies.CNY') || 'CNY - Китайский юань'}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.balance') || 'Баланс'}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.balance}
                onChange={(e) => setFormData({ ...formData, balance: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Выбор счета wFirma */}
            {hasWFirmaConfig && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('wfirma.accounts.linkWFirmaAccount') || 'Связать со счетом wFirma'}
                </label>
                <select
                  value={selectedWFirmaAccountId}
                  onChange={(e) => setSelectedWFirmaAccountId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">
                    {t('wfirma.accounts.selectAccount') || '-- Не связан --'}
                  </option>
                  {wfirmaAccounts
                    .filter(acc => acc.currency === formData.currency)
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name || (account.number && account.number !== '-' ? account.number : `ID: ${account.wfirmaId}`)} ({account.currency})
                      </option>
                    ))}
                </select>
                {wfirmaAccounts.filter(acc => acc.currency === formData.currency).length === 0 && wfirmaAccounts.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('wfirma.accounts.noMatchingCurrency') || 'Нет счетов wFirma с валютой'} {formData.currency}
                  </p>
                )}
                {wfirmaAccounts.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('wfirma.accounts.noWFirmaAccounts') || 'Нет синхронизированных счетов wFirma'}
                  </p>
                )}
              </div>
            )}
          </div>
        </form>

        <div className="flex justify-between p-6 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={handleDelete}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? (t('common.deleting') || 'Удаление...') : (t('common.delete') || 'Удалить')}
          </button>
          
          <div className="space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t('common.cancel') || 'Отмена'}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? (t('common.saving') || 'Сохранение...') : (t('common.save') || 'Сохранить')}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default EditBankAccountModal;
